import axiosInstance from "service/axiosInstance";
import {
  CrmAssignableTag,
  CrmTag,
  CrmTagPayload,
  CrmTagStatus,
  CrmTagTarget,
  CrmTagUsage,
  GetCrmTagsParams,
} from "types/crm/tag";
import { CrmPage } from "types/crm/pagination";
import { toCrmPage } from "./pagination";

/** The word the API wants typed back before it will delete a tag. Case-sensitive. */
export const CRM_TAG_DELETE_CONFIRMATION = "Delete";

export const tagQueryKeys = {
  all: ["crm-tags"] as const,
  lists: () => [...tagQueryKeys.all, "list"] as const,
  list: (params: GetCrmTagsParams) =>
    [...tagQueryKeys.lists(), params] as const,
  assignable: (recordType: CrmTagTarget) =>
    [...tagQueryKeys.all, "assignable", recordType] as const,
  usage: (tagUuid: string) => [...tagQueryKeys.all, "usage", tagUuid] as const,
};

export const tagApi = {
  /**
   * GET /crm/tags
   *
   * `status` is left off entirely for "All" — the API has no `ALL` member and
   * answers an unknown query enum with a 500 rather than a 400.
   */
  getTags: async ({
    search,
    status,
    page = 0,
    size = 20,
  }: GetCrmTagsParams = {}): Promise<CrmPage<CrmTag>> => {
    const { data } = await axiosInstance.get("/crm/tags", {
      params: {
        search: search?.trim() || undefined,
        status: status || undefined,
        page,
        size,
      },
    });
    return toCrmPage<CrmTag>(data.data, page, size);
  },

  /**
   * GET /crm/tags/assignable
   *
   * What a record's Tag field may offer: Active tags whose Applies To covers
   * the record type. Open to any CRM user.
   *
   * Deliberately not given the `search` param — the lists are small enough to
   * filter in the picker, and doing it there keeps typing instant.
   */
  getAssignableTags: async (
    recordType: CrmTagTarget,
  ): Promise<CrmAssignableTag[]> => {
    const { data } = await axiosInstance.get("/crm/tags/assignable", {
      params: { recordType },
    });
    return data.data ?? [];
  },

  /**
   * GET /crm/tags/{tagUuid}/usage
   *
   * The Contacts and Companies currently carrying the tag. Readable by any CRM
   * user, not just an admin — it is a view, and it changes nothing.
   */
  getTagUsage: async (tagUuid: string): Promise<CrmTagUsage> => {
    const { data } = await axiosInstance.get(`/crm/tags/${tagUuid}/usage`);
    return data.data;
  },

  createTag: async (payload: CrmTagPayload): Promise<CrmTag> => {
    const { data } = await axiosInstance.post("/crm/tags", payload);
    return data.data;
  },

  /** Full replace of name / appliesTo / colorKey. Leaves status and usage alone. */
  updateTag: async (
    tagUuid: string,
    payload: CrmTagPayload,
  ): Promise<CrmTag> => {
    const { data } = await axiosInstance.put(`/crm/tags/${tagUuid}`, payload);
    return data.data;
  },

  /** Mark Inactive / Make Active. Idempotent; existing assignments are untouched. */
  updateTagStatus: async (
    tagUuid: string,
    status: CrmTagStatus,
  ): Promise<CrmTag> => {
    const { data } = await axiosInstance.patch(`/crm/tags/${tagUuid}/status`, {
      status,
    });
    return data.data;
  },

  /**
   * DELETE /crm/tags/{tagUuid}
   *
   * The confirmation travels in a body on a DELETE, which axios only sends
   * under `data`. Sent as the canonical word rather than whatever the user
   * typed: the modal arms case-insensitively, the server does not.
   */
  deleteTag: async (tagUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/tags/${tagUuid}`, {
      data: { confirmation: CRM_TAG_DELETE_CONFIRMATION },
    });
  },
};
